import React from 'react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, MessageCircle } from 'lucide-react';

const ContactInfo = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const contactMethods = [
    {
      icon: MapPin,
      title: 'Ubicación',
      lines: [
        'Naranjal, Alto Paraná, Paraguay',
        'Zona Industrial - Km 12 Ruta PY02',
      ],
      color: 'var(--primary-500)',
    },
    {
      icon: Phone,
      title: 'Teléfono',
      lines: [
        '+595 61 XXXXXXX',
        '+595 975 XXXXXX (WhatsApp)',
      ],
      color: 'var(--accent-500)',
    },
    {
      icon: Mail,
      title: 'Correo Electrónico',
      lines: [
        'proyectos@carbontech.com.py',
        'info@carbontech.com.py',
      ],
      color: '#60a5fa',
    },
    {
      icon: Clock,
      title: 'Horario de Atención',
      lines: [
        'Lunes a Viernes: 07:00 - 17:00',
        'Sábados: 07:00 - 12:00',
      ],
      color: '#a78bfa',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: 0.2 }}
      ref={ref}
    >
      <h3 style={{
        fontFamily: 'var(--font-serif)',
        fontSize: '2.2rem',
        marginBottom: '20px',
      }}>
        Información de <span className="text-gradient">Contacto</span>
      </h3>

      <p style={{
        color: 'var(--text-secondary)',
        lineHeight: 1.8,
        marginBottom: '40px',
        fontSize: '1.05rem',
      }}>
        Nuestro equipo comercial está disponible para atenderle en español, portugués o inglés. 
        No dude en contactarnos para cualquier consulta sobre proyectos de carbono.
      </p>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}>
        {contactMethods.map((method, i) => (
          <motion.div
            key={method.title}
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '18px',
              padding: '24px',
              background: 'var(--dark-800)',
              borderRadius: '16px',
              border: '1px solid var(--border)',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
              e.currentTarget.style.transform = 'translateX(8px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            <div style={{
              width: '52px',
              height: '52px',
              background: `${method.color}15`,
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <method.icon size={24} style={{ color: method.color }} />
            </div>
            <div>
              <h4 style={{
                fontSize: '1.05rem',
                fontWeight: 600,
                marginBottom: '6px',
              }}>
                {method.title}
              </h4>
              {method.lines.map((line, j) => (
                <p key={j} style={{
                  color: 'var(--text-muted)',
                  fontSize: '0.9rem',
                  lineHeight: 1.5,
                }}>
                  {line}
                </p>
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      {/* WhatsApp CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, delay: 0.8 }}
        style={{
          marginTop: '32px',
          padding: '28px',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 46, 22, 0.3) 100%)',
          borderRadius: '16px',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          textAlign: 'center',
        }}
      >
        <MessageCircle size={32} style={{ color: 'var(--primary-400)', marginBottom: '12px' }} />
        <h4 style={{ fontSize: '1.1rem', marginBottom: '8px' }}>¿Prefiere WhatsApp?</h4>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '16px' }}>
          Escríbanos directamente para una respuesta más rápida
        </p>
        <a
          href="https://wa.me/595975000000"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 28px',
            background: 'var(--gradient-primary)',
            color: 'white',
            borderRadius: '50px',
            fontWeight: 600,
            fontSize: '0.95rem',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = 'var(--shadow-glow)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <MessageCircle size={18} />
          Iniciar Chat
        </a>
      </motion.div>
    </motion.div>
  );
};

export default ContactInfo;
